import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Box, Divider, Typography } from '@mui/material';
import React, { Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LANGUAGES } from '../../constants/languages';
import SettingsItem from '../../screens/settings/settings-item';
import BackButton from '../button/back-button';
import FlexBox from '../flexbox/flexbox';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../constants/routes';
import { DocumentLocation } from '../../api';
import { LegalDocumentType } from '../../models/legal-documents';
import { useGetLocalizedDate } from '../../utils/date/use-get-localized-date';

interface LegalDocumentsProps {
  type: LegalDocumentType;
  documentLocations?: DocumentLocation[];
}

const LegalDocuments: React.FC<LegalDocumentsProps> = ({
  type,
  documentLocations,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { getLocalizedDate } = useGetLocalizedDate();
  const previousPath = location.state?.from.pathname;
  const hasPreviousPath = typeof previousPath !== 'undefined';
  const navigate = useNavigate();
  const navigateBack = () => navigate(previousPath);
  const backButtonText = t(
    previousPath === ROUTES.SETTINGS ? 'settings.title' : 'common.back'
  );
  const title = t(
    type === LegalDocumentType.PRIVACY_POLICY
      ? 'common.privacy-policy'
      : 'settings.terms-conditions'
  );
  const spanishVersion = (documentLocations || []).find(
    (it) => it.lang === 'es'
  );
  const otherVersions = (documentLocations || []).filter(
    (it) => it.lang !== 'es'
  );
  const withDate = type === LegalDocumentType.TERMS_AND_CONDITIONS;
  const getLastEditedText = (date?: string) => {
    const localizedDate = getLocalizedDate(new Date(date || ''));
    return date && withDate
      ? `(${t('settings.last-edited')}: ${localizedDate})`
      : '';
  };

  return (
    <>
      {hasPreviousPath && (
        <FlexBox mt={3} ml={2}>
          <BackButton onClick={navigateBack}>{backButtonText}</BackButton>
        </FlexBox>
      )}
      <Box mt={3} mb={9} mx={4}>
        <Typography variant="h5">{title}</Typography>
        <Typography sx={{ my: 1.5, ml: 0.5 }}>
          {t('settings.official-version')}
        </Typography>
        {spanishVersion?.url && (
          <Link to={spanishVersion.url} target="_blank">
            <SettingsItem startIcon={<SaveAltIcon />} endIcon="">
              {`${t('languages.es')} ${getLastEditedText(
                spanishVersion?.lastModified
              )}`}
            </SettingsItem>
          </Link>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ my: 1.5, ml: 0.5 }}>
          {t('settings.translations')}
        </Typography>
        <Typography sx={{ my: 1.5, ml: 0.5 }}>
          {t('settings.translations-description', {
            document: title,
          })}
        </Typography>
        {otherVersions?.map((version) => (
          <Fragment key={version.lang}>
            {version.url &&
            version.lang &&
            Object.keys(LANGUAGES).includes(version.lang) ? (
              <Link to={version.url} target="_blank">
                <SettingsItem startIcon={<SaveAltIcon />} endIcon="">
                  {`${t(`languages.${version.lang}`)} ${getLastEditedText(
                    version.lastModified
                  )}`}
                </SettingsItem>
              </Link>
            ) : null}
          </Fragment>
        ))}
      </Box>
    </>
  );
};

export default LegalDocuments;
